import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, BarChart3, Share2, MessageSquare, Zap } from "lucide-react";

const exampleProfile = {
  headline: "Progressive centre-back with composure under pressure and range of passing into midfield zones.",
  position: "Centre-Back",
  age: "22",
  nationality: "England",
  foot: "Right",
  overview: "A left-footed centre-back comfortable in build-up phases, with the ability to play through the press and find progressive passing options. Strongest when operating in a back three, providing width and diagonal distribution.",
  attributes: {
    technical: ["Long-range distribution", "Ball-carrying under press"],
    tactical: ["High defensive line management", "Cover shadow positioning"],
    physical: ["Aerial dominance", "Recovery pace over 10-20m"],
    mental: ["Vocal organiser", "Calm in transition"],
  },
};

const WHAT_YOU_GET = [
  { icon: CheckCircle, text: "A structured professional player profile" },
  { icon: MessageSquare, text: "Clear football identity" },
  { icon: Zap, text: "Guided improvement" },
  { icon: BarChart3, text: "Profile strength scoring" },
  { icon: Share2, text: "A shareable link for clubs and scouts" },
];

const STEPS = [
  { num: "01", title: "ANSWER GUIDED QUESTIONS", desc: "An intelligent system asks targeted football questions to understand your playing identity, strengths, and experience." },
  { num: "02", title: "BUILD AND REFINE YOUR PROFILE", desc: "Your answers are structured into a professional scouting report. Edit directly or use AI to improve each section." },
  { num: "03", title: "USE IT WHERE IT MATTERS", desc: "Share your profile with scouts, agents, and clubs. Use it for trials, applications, and recruitment enquiries." },
];

export default function GetProfile() {
  return (
    <div className="bg-[#0A0A0A]" data-testid="get-profile-page">

      {/* SECTION 1 — HERO */}
      <section className="py-24 sm:py-32" data-testid="gp-hero">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter uppercase text-[#F5F5F5] leading-[0.95] mb-6" data-testid="gp-headline">
            THIS IS HOW YOU<br />POSITION YOURSELF<br /><span className="text-[#D4AF37]">PROPERLY</span>
          </h1>
          <p className="text-base text-[#A3A3A3] mb-3 max-w-md mx-auto leading-relaxed">
            Most players guess how to present themselves.
          </p>
          <p className="text-sm text-[#777] mb-10 max-w-md mx-auto">
            This gives you structure.
          </p>
          <Link to="/start" className="btn-gold inline-flex items-center gap-2 text-base" data-testid="gp-hero-cta">
            GET YOUR PROFILE <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* SECTION 2 — WHAT YOU GET */}
      <section className="py-20 border-t border-[#262626]" data-testid="gp-what-you-get">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <div className="overline mb-4">INCLUDED</div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight uppercase text-[#F5F5F5]">
              WHAT YOU GET
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-0 max-w-5xl mx-auto">
            {WHAT_YOU_GET.map((item, i) => (
              <div key={i} className="p-5 border border-[#262626] text-center" data-testid={`wyg-${i}`}>
                <item.icon size={20} className="text-[#D4AF37] mx-auto mb-3" strokeWidth={1.5} />
                <p className="text-[11px] text-[#E5E5E5] leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3 — EXAMPLE PROFILE */}
      <section className="py-20 border-t border-[#262626]" data-testid="gp-example">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-8">
            <div className="overline mb-4">PREVIEW</div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight uppercase text-[#F5F5F5]">
              THIS IS HOW PLAYERS<br />ARE STRUCTURED<br />INSIDE THE NETWORK
            </h2>
            <p className="text-sm text-[#777] mt-3">This is what serious players use.</p>
          </div>
          <div className="max-w-3xl mx-auto card-dark" data-testid="example-profile-card">
            <div className="p-6 border-b border-[#262626]">
              <div className="flex items-center gap-2 mb-2">
                <span className="badge-gold">{exampleProfile.position}</span>
                <span className="text-xs text-[#A3A3A3]">{exampleProfile.age} yrs</span>
                <span className="text-xs text-[#A3A3A3]">{exampleProfile.nationality}</span>
                <span className="text-xs text-[#A3A3A3]">{exampleProfile.foot} foot</span>
              </div>
              <h3 className="text-[#D4AF37] font-semibold text-sm leading-relaxed">{exampleProfile.headline}</h3>
            </div>
            <div className="p-6 border-b border-[#262626]">
              <div className="text-[9px] font-bold text-[#D4AF37] uppercase tracking-widest mb-2">OVERVIEW</div>
              <p className="text-sm text-[#ccc] leading-relaxed">{exampleProfile.overview}</p>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-0">
              {Object.entries(exampleProfile.attributes).map(([cat, attrs]) => (
                <div key={cat} className="p-5 border-r border-b border-[#262626] last:border-r-0">
                  <div className="text-[9px] font-bold text-[#D4AF37] uppercase tracking-widest mb-3">{cat}</div>
                  <ul className="space-y-1.5">
                    {attrs.map((a, j) => (
                      <li key={j} className="text-xs text-[#E5E5E5] flex items-start gap-1.5">
                        <div className="w-1 h-1 bg-[#D4AF37] rounded-full mt-1.5 flex-shrink-0" />{a}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="p-5 border-t border-[#262626]">
              <div className="bg-[#0E0E0E] border border-[#1A1A1A] h-44 flex items-center justify-center">
                <span className="text-sm text-[#555] uppercase tracking-wider">Highlight Video</span>
              </div>
            </div>
          </div>
          <p className="text-sm text-[#A3A3A3] text-center mt-6">
            Clear. Structured. Easy to assess.
          </p>
        </div>
      </section>

      {/* SECTION 4 — HOW IT WORKS */}
      <section className="py-20 border-t border-[#262626]" data-testid="gp-how">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <div className="overline mb-4">PROCESS</div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight uppercase text-[#F5F5F5]">
              HOW IT WORKS
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 max-w-4xl mx-auto">
            {STEPS.map((step, i) => (
              <div key={i} className="p-8 border border-[#262626]" data-testid={`step-${i}`}>
                <div className="text-2xl font-black text-[#D4AF37]/30 mb-4">{step.num}</div>
                <h3 className="text-sm font-bold tracking-wider text-[#F5F5F5] mb-3">{step.title}</h3>
                <p className="text-sm text-[#A3A3A3] leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5 — SPEED / CERTAINTY */}
      <section className="py-20 border-t border-[#262626] bg-[#0E0E0E]" data-testid="gp-speed">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight uppercase text-[#F5F5F5] mb-5">
            TAKES A FEW MINUTES
          </h2>
          <p className="text-sm text-[#A3A3A3] leading-relaxed max-w-xl mx-auto">
            The system guides you step-by-step and ensures your profile is clear, complete, and ready to use.
          </p>
        </div>
      </section>

      {/* SECTION 6 — NETWORK CONNECTION */}
      <section className="py-24 border-t border-[#262626]" data-testid="gp-network">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="overline mb-4">USE IT WHERE IT MATTERS</div>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight uppercase text-[#F5F5F5] mb-6">
            INSIDE THE NETWORK<br />OR REACHING OUT DIRECTLY
          </h2>
          <p className="text-base text-[#A3A3A3] leading-relaxed max-w-xl mx-auto">
            Your profile sits within a structured football network where players are positioned for visibility and assessment.
          </p>
        </div>
      </section>

      {/* SECTION 7 — FINAL CTA */}
      <section className="py-24 border-t border-[#262626] bg-[#0E0E0E]" data-testid="gp-final-cta">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <p className="text-base text-[#A3A3A3] mb-8 leading-relaxed">
            Start positioning yourself properly.
          </p>
          <Link to="/start" className="btn-gold inline-flex items-center gap-2 text-base" data-testid="gp-final-cta-btn">
            START POSITIONING YOURSELF PROPERLY <ArrowRight size={18} />
          </Link>
          <p className="text-xs text-[#777] mt-4">$19. One-off. No subscription.</p>
          <p className="text-[10px] text-[#555] mt-2">Start now. Takes a few minutes.</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 border-t border-[#262626]">
        <div className="max-w-7xl mx-auto px-4 flex flex-col items-center gap-5">
          <img src="https://customer-assets.emergentagent.com/job_elite-scout-mvp/artifacts/uw8m7f8v_Simple%20Sports%20Football%20%26%20Soccer%20Badge%20Logo.png" alt="Elite Scouting Network" className="h-14 w-14 rounded-full opacity-80" />
          <span className="text-[#F5F5F5] font-bold text-sm tracking-tight uppercase" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Elite Scouting Network</span>
          <div className="flex gap-6 text-xs">
            <Link to="/" className="text-[#A3A3A3] hover:text-[#F5F5F5] transition-colors">Home</Link>
            <Link to="/directory" className="text-[#A3A3A3] hover:text-[#F5F5F5] transition-colors">Directory</Link>
            <Link to="/scout-apply" className="text-[#A3A3A3] hover:text-[#F5F5F5] transition-colors">Scout Access</Link>
            <Link to="/pricing" className="text-[#A3A3A3] hover:text-[#F5F5F5] transition-colors">Pricing</Link>
          </div>
          <span className="text-[#555] text-[10px] tracking-wider">All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}
